import constants from "@/libs/Constants";
import {yearMonthToStr, monthToStringFormat, UUIDfromList, monthNumberToLabelI18N} from '@/libs/Helpers';


/* ----------------------
 * Variable configuration
 * ---------------------- */
export class VariableConfiguration {
    /**
     * Class constructor
     *
     * @param id {String} ID of variable
     * @param name_i18n {Object} List of names by language
     * @param palette {VariableColorConfigurationAbstract}
     * @param unit {String} Unit of measurement for variable
     * @param decimals {Number} Number of decimals to round value
     * @param area_multiple_selection {Boolean} Whether the variable accepts the selection of multiple areas or not
     * @param colors_percentile {Array} Optional: List of colors for percentiles. Default = null
     * @param background_colors {Array} Optional: List of colors for background in plots. Default = null
     */
    constructor(id, name_i18n, palette, unit, decimals, area_multiple_selection, colors_percentile = null, background_colors = null) {
        this.id = id.toString();
        this.name_i18n = name_i18n;
        this.palette = palette;
        this.unit = unit;
        this.decimals = decimals;
        this.area_multiple_selection = area_multiple_selection !== true && area_multiple_selection !== false ?
            true :
            area_multiple_selection;
        this.colors_percentile = colors_percentile;
        this.background_colors = background_colors;
    }

    getNameI18N(language) {
        let name = '';

        if (this.name_i18n !== undefined) {
            name = this.name_i18n[language]
            if (name === undefined)
                name = this.name_i18n.en;
        }

        return name !== undefined ? name : '';
    }

    _buildRGBA(colorItems, alpha = 0.1) {
        let color = null;
        if (colorItems !== undefined && colorItems.length === 3)
            color = `rgba(${colorItems.join(',')},${alpha})`;
        return color;
    }

    getBackgroundAboveRGBA(alpha = 0.1) {
        return this.background_colors !== null ?
            this._buildRGBA(this.background_colors.above, alpha) :
            null;
    }

    getBackgroundBelowRGBA(alpha = 0.1) {
        return this.background_colors !== null ?
            this._buildRGBA(this.background_colors.below, alpha) :
            null;
    }
}

export class VisualizationGeoJsonDataAbstract {
    /**
     * Class constructor
     * @param variableConfiguration {VariableConfiguration}
     * @param geoJson {Object}
     */
    constructor(variableConfiguration, geoJson) {
        this.component = null;
        this.variableConfiguration = variableConfiguration;
        this.geoJson = geoJson;
    }

    get variable() {
        return this.variableConfiguration.id;
    }
}

export class VisualizationMonitoringData extends VisualizationGeoJsonDataAbstract {
    /**
     * Class constructor
     *
     * @param variableConfiguration {VariableConfiguration} Configuration for variable
     * @param month {Number} Selected month
     * @param year {Number} Selected year
     * @param monthConfiguration Month configuration object
     * @param geoJson {Object} GeoJSON data
     */
    constructor(variableConfiguration, month, year, monthConfiguration, geoJson) {
        super(variableConfiguration, geoJson);

        this.component = constants.COMPONENTS.MONITORING;
        this.month = month;
        this.year = year;

        //Month configuration for visualization variable
        this.monthConfiguration = monthConfiguration;
    }

    monthYearI18N(i18n) {
        return `${monthNumberToLabelI18N(this.month, i18n)}/${this.year}`;
    }

    get monthYear() {
        return `${monthToStringFormat(this.month)}/${this.year}`;
    }


    get yearMonthStr() {
        return yearMonthToStr(this.year, this.month);
    }

    /**
     * UUID for current object
     * @returns {string | Uint8Array | *}
     */
    get uuid() {
        let items = [this.component, this.variable, this.month, this.year];
        return UUIDfromList(items);
    }
}

export class VisualizationForecastData extends VisualizationGeoJsonDataAbstract {
    constructor(variableConfiguration, creationMonth, creationYear, forecastMonth, forecastYear, percentile, geoJson, visualizationMode = constants.VISUALIZATION_MODE.ADVANCED) {
        super(variableConfiguration, geoJson);

        this.component = constants.COMPONENTS.FORECAST;
        this.percentile = percentile;

        //Creation month
        this.creationMonth = creationMonth;
        this.creationYear = creationYear;

        //Forecast month
        this.forecastMonth = forecastMonth;
        this.forecastYear = forecastYear;

        this.visualizationMode = visualizationMode;
    }

    creationMonthYearI18N(i18n) {
        return `${monthNumberToLabelI18N(this.creationMonth, i18n)}/${this.creationYear}`;
    }

    forecastMonthYearI18N(i18n) {
        return `${monthNumberToLabelI18N(this.forecastMonth, i18n)}/${this.forecastYear}`;
    }

    get creationMonthYear() {
        return `${monthToStringFormat(this.creationMonth)}/${this.creationYear}`;
    }

    get creationYearMonthStr() {
        return yearMonthToStr(this.creationYear, this.creationMonth);
    }

    get forecastMonthYear() {
        return `${monthToStringFormat(this.forecastMonth)}/${this.forecastYear}`;
    }

    get forecastYearMonthStr() {
        return yearMonthToStr(this.forecastYear, this.forecastMonth);
    }

    /**
     * UUID for current object
     * @returns {string | Uint8Array | *}
     */
    get uuid() {
        let items = [this.component, this.variable, this.percentile, this.creationMonthYear, this.forecastMonthYear];
        return UUIDfromList(items);
    }
}

export class VisualizationProjectionData extends VisualizationGeoJsonDataAbstract {
    constructor(variableConfiguration, year, season, climateScenario, geoJson, visualizationMode = constants.VISUALIZATION_MODE.ADVANCED) {
        super(variableConfiguration, geoJson);

        this.component = constants.COMPONENTS.PROJECTION;
        this.year = year;
        this.season = season;
        this.climateScenario = climateScenario;
        this.visualizationMode = visualizationMode;
    }

    get climateScenarioLabel() {
        return this.climateScenario.toUpperCase();
    }

    climateScenarioLabelI18N(i18n) {
        let mapScenarioLabel = {
            'SSP1-2.6': 'InputSSPLabel.SSP1_2-6',
            'SSP2-4.5': 'InputSSPLabel.SSP2_4-5',
            'SSP5-8.5': 'InputSSPLabel.SSP5_8-5'
        }

        return mapScenarioLabel.hasOwnProperty(this.climateScenarioLabel) ?
            i18n.t(mapScenarioLabel[this.climateScenarioLabel])
            : this.climateScenarioLabel;
    }

    /**
     * UUID for current object
     * @returns {string | Uint8Array | *}
     */
    get uuid() {
        let items = [this.component, this.variable, this.year, this.season, this.climateScenario];
        return UUIDfromList(items);
    }
}